import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useRef, useState } from "react";
import { useCustomerValue } from "./CustomerContext";
import { getSntStatus, getTAHasPriority } from "services/data.outbound.service";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const StatusContext = createContext(null);

export function useStatusValue() {
  return useContext(StatusContext);
}

export function StatusProvider({ children }) {
  const { customerCode } = useCustomerValue();
  const [data, setData] = useState({
    inboundError: false,
    outboundError: false,
    esoError: false,
    pickError: false,
    recvError: false,
    returnError: false,
    putawayOrderAvailable:false,
    hasPriorityOrder: false,
  });
  const [priorityAlertOpen, setPriorityAlertOpen] = useState(false);
  // tracks the customerCode we've already alerted for, so we don't re-alert on every 2-min poll
  // while it stays true, but do alert again if it goes away and comes back later
  const alertedCustomerRef = useRef(null);

  useQuery({
    queryKey: ["snt-status", { customerCode }],
    queryFn: () => getSntStatus({ customerCode }),
    onSuccess: (res) => {
      setData((prev) => ({ ...prev, ...res.data.data }));
    },
    enabled: Boolean(customerCode),
  });
  useQuery({
    queryKey: ["task-assignment-has-priority", { customerCode }],
    queryFn: () => getTAHasPriority({ customerCode }),
    onSuccess: (res) => {
      const hasPriorityOrder = res.data.data;
      setData((prev) => ({ ...prev, hasPriorityOrder }));
      if (hasPriorityOrder && alertedCustomerRef.current !== customerCode) {
        setPriorityAlertOpen(true);
        alertedCustomerRef.current = customerCode;
      } else if (!hasPriorityOrder) {
        alertedCustomerRef.current = null;
      }
    },
    enabled: Boolean(customerCode),
    refetchInterval: 120000,
  });
  const providerValue = useMemo(() => {
    return { data };
  }, [data]);
  return (
    <StatusContext.Provider value={providerValue}>
      {children}
      <Snackbar
        open={priorityAlertOpen}
        autoHideDuration={5000}
        onClose={() => setPriorityAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setPriorityAlertOpen(false)} severity="warning">
          This customer has priority / instant delivery order(s) pending assignment.
        </Alert>
      </Snackbar>
    </StatusContext.Provider>
  );
}
